<script lang="ts">
import {
  type ComputedRef,
  type Ref,
  computed,
  defineComponent,
  getCurrentInstance,
  inject,
  onMounted,
  onUnmounted,
  readonly,
  ref,
  toRaw,
  useTemplateRef,
  watch,
  watchEffect
} from 'vue'
import { ICON_TYPE, type TitleBarOptionsType } from '@/types/share'
import bus from '@/utils/bus'
import { invoke } from '@kiti/js-bridge'
import router from '@/router'
import { thruttle } from '@/utils/lang'
import { useRoute } from 'vue-router'

import backArrowPng from '@/assets/img/back-arrow-black.png'
import backArrowWhitePng from '@/assets/img/nav_back_arrow_white.png'

// 白天图标
const iconMap: Partial<Record<ICON_TYPE, string>> = {
  [ICON_TYPE.BACK_ARROW]: backArrowPng
}
// 夜间图标
const iconDarkModeMap: Partial<Record<ICON_TYPE, string>> = {
  [ICON_TYPE.BACK_ARROW]: backArrowWhitePng
}

export default defineComponent({
  props: {
    title: String
  },
  setup() {
    const app = getCurrentInstance()?.appContext
    const curTheme = app?.config.globalProperties.$pageTheme.curTheme || ref<string>('light')
    // 默认标题栏配置
    const defaultOptions = ref<TitleBarOptionsType>({
      hideNativeBar: false,
      isImmersive: false,
      theme: 'light',
      titleBar: {
        isHidden: true,
        isTransparent: false,
        left: [
          {
            iconUrl: ICON_TYPE.BACK_ARROW,
            text: '',
            callback: () => {
              router.go(-1)
            }
          }
        ],
        center: '',
        right: [],
        scrollBgColor: 'light'
      },
      statusBar: {
        isHidden: true
      }
    })

    // 路由标题栏配置
    const currentRoute = useRoute()
    const routeLayoutOptions = ref<TitleBarOptionsType | null>(null)

    watchEffect(() => {
      const layoutConfig = currentRoute.meta.layout as TitleBarOptionsType

      routeLayoutOptions.value = layoutConfig
    })

    // 用户手动标题栏配置
    const userLayoutOptions = ref<TitleBarOptionsType | null>(null)

    function setTitleBarOptions(layoutConfig: TitleBarOptionsType): void {
      userLayoutOptions.value = layoutConfig
    }
    window.setTitleBarOptions = setTitleBarOptions
    bus.on('setTitleBarOptions', setTitleBarOptions)

    // 合并标题栏配置
    type FinalTitleBarOptionsType = TitleBarOptionsType & {
      computedThemeValue?: string
      titleBar: TitleBarOptionsType['titleBar'] & {
        left: {
          iconUrlRender?: string
          text?: string
          callback?: () => void
        }[]
        right: {
          iconUrlRender?: string
          text?: string
          callback?: () => void
        }[]
        renderCenter?: TitleBarOptionsType['titleBar']['center']
      }
    }
    const finalTitleBarOptions: ComputedRef<FinalTitleBarOptionsType> = computed(() => {
      const routeOptions = routeLayoutOptions.value
      const userOptions = userLayoutOptions.value
      const defaultOptionsValue = defaultOptions.value
      const res: FinalTitleBarOptionsType = {
        hideNativeBar:
          userOptions?.hideNativeBar ??
          routeOptions?.hideNativeBar ??
          defaultOptionsValue.hideNativeBar,
        isImmersive:
          userOptions?.isImmersive ?? routeOptions?.isImmersive ?? defaultOptionsValue.isImmersive,
        theme: userOptions?.theme ?? routeOptions?.theme ?? defaultOptionsValue.theme,
        titleBar: {
          isHidden:
            routeOptions?.titleBar?.isHidden ??
            userOptions?.titleBar?.isHidden ??
            defaultOptionsValue.titleBar.isHidden,
          isTransparent:
            userOptions?.titleBar?.isTransparent ??
            routeOptions?.titleBar?.isTransparent ??
            defaultOptionsValue.titleBar.isTransparent,
          left: [
            ...(userOptions?.titleBar?.left ??
              routeOptions?.titleBar?.left ??
              defaultOptionsValue.titleBar.left ??
              [])
          ],
          center:
            userOptions?.titleBar?.center ??
            routeOptions?.titleBar?.center ??
            defaultOptionsValue.titleBar.center,
          right: [
            ...(userOptions?.titleBar?.right ??
              routeOptions?.titleBar?.right ??
              defaultOptionsValue.titleBar.right ??
              [])
          ],
          scrollBgColor:
            userOptions?.titleBar?.scrollBgColor ??
            routeOptions?.titleBar?.scrollBgColor ??
            defaultOptionsValue.titleBar.scrollBgColor
        },
        statusBar: {
          isHidden:
            routeOptions?.statusBar?.isHidden ??
            userOptions?.statusBar?.isHidden ??
            defaultOptionsValue?.statusBar?.isHidden
        }
      }

      if (res.titleBar.scrollBgColor === 'light') {
        res.titleBar.scrollBgColor = 'var(--basic-background-color)'
      }
      type RenderIconType = FinalTitleBarOptionsType['titleBar']['left'][number]
      const computedThemeValue = curTheme.value

      res.computedThemeValue = computedThemeValue
      if (res.titleBar.left) {
        res.titleBar.left.forEach(item => {
          if (typeof item.iconUrl === 'number') {
            ;(item as RenderIconType).iconUrlRender =
              computedThemeValue === 'dark' ? iconDarkModeMap[item.iconUrl] : iconMap[item.iconUrl]
          } else {
            ;(item as RenderIconType).iconUrlRender = item.iconUrl
          }
        })
      }
      if (res.titleBar.right) {
        res.titleBar.right.forEach(item => {
          if (typeof item.iconUrl === 'number') {
            ;(item as RenderIconType).iconUrlRender =
              computedThemeValue === 'dark' ? iconDarkModeMap[item.iconUrl] : iconMap[item.iconUrl]
          } else {
            ;(item as RenderIconType).iconUrlRender = item.iconUrl
          }
        })
      }
      if (res.titleBar.center) {
        res.titleBar.renderCenter = toRaw(res.titleBar.center)
      }
      return res
    })

    const isDefaultTransparent = computed(
      () =>
        finalTitleBarOptions.value.isImmersive && finalTitleBarOptions.value.titleBar.isTransparent
    )

    // 监听标题栏配置变化，调用客户端bridge完成布局设置
    const globalLayoutInfo = ref({
      statusBar: {
        height: '14px'
      },
      titleBar: {
        height: '49px'
      },
      twoBar: {
        height: '63px'
      }
    })

    const provideLayout = inject('layoutInfo')

    ;(
      provideLayout as Ref<{
        statusBar: {
          height: string
        }
        titleBar: {
          height: string
        }
        twoBar: {
          height: string
        }
      }>
    ).value = globalLayoutInfo.value

    if (app) {
      app.config.globalProperties.$layoutInfo = readonly(globalLayoutInfo)
    }
    const setNativeLayout = function () {
      // 直接沉浸式
      const computedTheme = finalTitleBarOptions.value.computedThemeValue

      invoke<{
        titleBar: {
          height: number
        }
        statusBar: {
          height: number
        }
      }>('setLayoutOptions', {
        titleBar: {
          isHidden: finalTitleBarOptions.value.hideNativeBar
        },
        statusBar: {
          theme: computedTheme
        }
      })
        .catch(() => {})
        .then(async topBarSizeInfo => {
          // 设置布局信息到全局app
          const deviceRatio = window.devicePixelRatio

          if (topBarSizeInfo) {
            globalLayoutInfo.value.statusBar.height =
              topBarSizeInfo.statusBar.height / deviceRatio + 'px'
            globalLayoutInfo.value.titleBar.height =
              topBarSizeInfo.titleBar.height / deviceRatio + 'px'
            globalLayoutInfo.value.twoBar.height =
              (topBarSizeInfo.titleBar.height + topBarSizeInfo.statusBar.height) / deviceRatio +
              'px'
          }
        })
    }

    watch(
      [
        () => finalTitleBarOptions.value.computedThemeValue,
        () => finalTitleBarOptions.value.hideNativeBar
      ],
      async () => {
        setNativeLayout()
      }
    )

    // 实现滚动实色效果
    const topBarRef = useTemplateRef<HTMLElement>('topBar')
    const scrollNoTransparent = ref(false)
    const scrollHandler = thruttle(function (e: unknown) {
      const targetWrap = (e as Event).target

      // 只有沉浸式且需要滚动实色的时候才需要做
      if (
        isDefaultTransparent.value &&
        finalTitleBarOptions.value.titleBar.scrollBgColor !== 'no'
      ) {
        const scrollTop = (targetWrap as HTMLElement)?.scrollTop
        const topBarHeight = topBarRef?.value?.offsetHeight

        if (scrollTop && topBarHeight && scrollTop > topBarHeight) {
          scrollNoTransparent.value = true
        } else {
          scrollNoTransparent.value = false
        }
      }
    }, 200)

    onMounted(() => {
      bus.on('mainScroll', scrollHandler)
    })
    onUnmounted(() => {
      bus.off('mainScroll', scrollHandler)
    })
    const isTransparent = computed(() => isDefaultTransparent.value && !scrollNoTransparent.value)

    return { finalTitleBarOptions, isTransparent, globalLayoutInfo }
  }
})
</script>

<template>
  <div
    ref="topBar"
    class="comp-title-bar"
    :class="{ immersive: finalTitleBarOptions.isImmersive, transparent: isTransparent }"
    :data-theme="finalTitleBarOptions.computedThemeValue"
    :style="
      !isTransparent && finalTitleBarOptions.titleBar.scrollBgColor
        ? { backgroundColor: finalTitleBarOptions.titleBar.scrollBgColor }
        : {}
    "
  >
    <div
      v-if="!finalTitleBarOptions?.statusBar?.isHidden"
      class="status-bar-wrap"
      :style="{ height: $layoutInfo.value.statusBar.height }"
    />

    <div
      v-if="!finalTitleBarOptions.titleBar.isHidden"
      class="title-bar-wrap"
      :style="{ height: $layoutInfo.value.titleBar.height }"
    >
      <div class="left-info">
        <div
          v-for="(item, idx) of finalTitleBarOptions.titleBar.left"
          :key="idx"
          class="item"
          @click="item?.callback?.()"
        >
          <img
            v-if="item.iconUrlRender"
            :src="item.iconUrlRender"
            alt=""
          />

          <span>{{ item.text }}</span>
        </div>
      </div>

      <div class="center-info">
        <span v-if="typeof finalTitleBarOptions.titleBar.center === 'string'">{{
          finalTitleBarOptions.titleBar.center
        }}</span>

        <component
          :is="finalTitleBarOptions.titleBar.renderCenter"
          v-else-if="finalTitleBarOptions.titleBar.renderCenter"
        />
      </div>

      <div class="right-info">
        <div
          v-for="(item, idx) of finalTitleBarOptions.titleBar.right"
          :key="idx"
          class="item"
          @click="item?.callback?.()"
        >
          <span>{{ item.text }}</span>

          <img
            v-if="item.iconUrlRender"
            :src="item.iconUrlRender"
            alt=""
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.comp-title-bar {
  background-color: var(--basic-background-color);
  color: #121212;
  z-index: 20;
  // transition: background-color .1s;

  .status-bar-wrap {
    height: 14rem;
  }

  .title-bar-wrap {
    text-align: center;
    display: flex;
    align-items: center;
    font-size: 14rem;
    height: 49rem;
    padding: 0 12rem;
    overflow: hidden;
    justify-content: space-between;
    position: relative;

    .left-info,
    .right-info {
      display: flex;
      align-items: center;
      z-index: 200;

      .item {
        display: flex;
        align-items: center;

        img {
          width: 32rem;
          height: 32rem;
        }
      }
    }

    .center-info {
      flex: 1;
      position: absolute;
      z-index: 100;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 18rem;
      color: #121212;
      line-height: 22rem;
    }
  }
}

// 深色样式
.comp-title-bar[data-theme='dark'] {
  background-color: #121212;
  color: #fff;

  .title-bar-wrap {
    .center-info {
      color: white;
    }
  }
}

// 沉浸式样式--高优先级
.comp-title-bar.immersive {
  position: fixed;
  right: 0;
  left: 0;
  top: 0;
}

.comp-title-bar.transparent {
  background-color: transparent;
}

// rtl
[dir='rtl'] .comp-title-bar .title-bar-wrap {
  .left-info {
    img {
      transform: scaleX(-1);
    }
  }
}
</style>
